'use client';

import { Box, Container, Heading, Text, VStack, HStack, Link, List, Separator, Stack, Icon } from '@chakra-ui/react';
import { CheckCircle, AlertCircle, FileTextIcon, ShieldAlertIcon, MailIcon, InfoIcon } from 'lucide-react';
import React, { useContext } from 'react';
import { WireframeContext } from 'contexts/wireframeContext';
import { ROUTES } from 'services/shared/routerService';
import Breadcrumb from 'components/breadcrumb';
import SEO from 'components/seo';

export default function LegalNoticePage() {
	const { wireframe, setWireframe } = useContext(WireframeContext);

	return (
		<Box as="main" bg="bg.canvas" minH="100vh">
			<SEO
				title="Imagemotion: Legal Notice"
				description="Create viral ads with ai for e-commerce and dropshipping. Scale with winning video and image ads."
			/>
			<Box as="section" pt="12" pb="12" px="4" bgGradient="to-br" gradientFrom="accent/90" gradientTo="accent/80">
				<Container maxW="3xl" textAlign="center">
					<Heading as="h1" size="3xl" color="white" mb="3" fontWeight="extrabold">
						Legal Notice
					</Heading>
					<Text fontSize="md" color="whiteAlpha.800" maxW="xl" mx="auto">
						Terms of Service and legal information for using Imagemotion.
					</Text>
					<Text fontSize="xs" color="whiteAlpha.500" mt="4">
						Last updated: April 2026
					</Text>
				</Container>
			</Box>

			<Box as="section" py="8" bg="bg.muted" borderBottomWidth="1px" position="sticky" top="10" zIndex="10">
				<Container maxW="3xl">
					<HStack gap="3" wrap="wrap" justify="center">
						{[
							'Terms of Service',
							'Acceptable Use',
							'Intellectual Property',
							'Liability',
							'Contact',
							'Legal Info',
						].map((item) => (
							<Link
								key={item}
								href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
								px="3.5"
								py="1.5"
								bg="bg.panel"
								rounded="full"
								fontSize="xs"
								fontWeight="medium"
								textDecoration="none"
								boxShadow="xs"
								_hover={{ bg: 'blue.600', color: 'white' }}
								transition="all 0.2s"
							>
								{item}
							</Link>
						))}
					</HStack>
				</Container>
			</Box>

			<Container as="section" maxW="2xl" py="12" px="4">
				<Breadcrumb
					navigation={[
						{ label: 'Home', href: ROUTES.HOME },
						{ label: 'Legal' },
						{ label: 'Legal Notice', href: ROUTES.LEGALNOTICE },
					]}
				/>
				<VStack gap="12" align="stretch" mt={12}>
					<Box id="terms-of-service">
						<HStack gap="2" mb="3">
							<Icon as={FileTextIcon} boxSize={4} color="blue.500" />
							<Heading as="h2" size="md">
								Terms of Service
							</Heading>
						</HStack>
						<VStack align="start" gap="3" fontSize="sm" color="fg.muted">
							<p>
								Welcome to {process.env.NEXT_PUBLIC_APP_NAME}. By accessing or using our service, you
								agree to be bound by these Terms of Service and all applicable laws and regulations. If
								you do not agree with any of these terms, you are prohibited from using or accessing
								this site.
							</p>
							<p>
								These Terms of Service apply to all visitors, users, and others who access or use the
								Service.
							</p>
						</VStack>
					</Box>

					<Box id="acceptable-use">
						<HStack gap="2" mb="3">
							<Icon as={ShieldAlertIcon} boxSize={4} color="blue.500" />
							<Heading as="h2" size="md">
								Acceptable Use Policy
							</Heading>
						</HStack>
						<Text fontSize="sm" color="fg.muted" mb="4">
							You agree to use {process.env.NEXT_PUBLIC_APP_NAME} only for lawful purposes. Prohibited
							behaviors include:
						</Text>

						<Stack gap="4">
							<Box
								bg="red.50"
								_dark={{ bg: 'red.950/20' }}
								border="1px solid"
								borderColor="red.100"
								rounded="xl"
								p="5"
							>
								<Text fontWeight="bold" color="red.700" mb="2" fontSize="xs" textTransform="uppercase">
									Prohibited Uses
								</Text>
								<List.Root gap="2">
									{[
										'To generate illegal, harmful or offensive content',
										'To infringe upon intellectual property rights of others',
										'To create deepfakes or misleading content',
										'To harass, abuse or harm another person',
										'To violate any applicable laws or regulations',
									].map((item, i) => (
										<List.Item
											key={i}
											display="flex"
											alignItems="start"
											gap="2"
											fontSize="xs"
											color="red.700"
										>
											<AlertCircle size={14} style={{ marginTop: '2px' }} />
											{item}
										</List.Item>
									))}
								</List.Root>
							</Box>

							<Box
								bg="green.50"
								_dark={{ bg: 'green.950/20' }}
								border="1px solid"
								borderColor="green.100"
								rounded="xl"
								p="5"
							>
								<Text
									fontWeight="bold"
									color="green.700"
									mb="2"
									fontSize="xs"
									textTransform="uppercase"
								>
									Permitted Uses
								</Text>
								<List.Root gap="2">
									{[
										'Creating original images and videos for personal or commercial use',
										'Generating creative content, artwork, illustrations, and visual concepts',
										'Creating marketing, advertising, promotional, and social media content',
										'Producing visual assets for websites, presentations, products, and other projects',
										'Generating educational, informational, and research materials',
										'Creating entertainment and storytelling content, including characters, scenes, and animations',
										'Exploring creative ideas, concepts, and visual styles',
										'Editing, transforming, or enhancing content where you have the necessary rights or permissions',
										'Using generated content for lawful professional, business, or personal purposes',
									].map((item, i) => (
										<List.Item
											key={i}
											display="flex"
											alignItems="start"
											gap="2"
											fontSize="xs"
											color="green.700"
										>
											<CheckCircle size={14} style={{ marginTop: '2px' }} />
											{item}
										</List.Item>
									))}
								</List.Root>
							</Box>
						</Stack>
					</Box>

					<Box id="intellectual-property">
						<Heading as="h2" size="md" mb="3">
							Intellectual Property
						</Heading>
						<Text fontSize="sm" color="fg.muted" mb="3">
							The Service and its original content, features, and functionality remain the exclusive
							property of {process.env.NEXT_PUBLIC_APP_NAME} and its licensors.
						</Text>
						<Text fontSize="sm" color="fg.muted">
							Content you generate using our Service belongs to you, subject to our usage terms.
						</Text>
					</Box>

					<Box id="liability">
						<Heading as="h2" size="md" mb="3">
							Limitation of Liability
						</Heading>
						<Box bg="bg.muted" rounded="xl" p="5" borderLeftWidth="4px" borderLeftColor="blue.500">
							<Text
								fontSize="xs"
								color="fg.muted"
								fontStyle="italic"
								lineHeight="tall"
								textTransform={'uppercase'}
							>
								THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS.{' '}
								{process.env.NEXT_PUBLIC_APP_NAME} MAKES NO WARRANTIES, EXPRESSED OR IMPLIED, INCLUDING
								WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE.
							</Text>
						</Box>
					</Box>

					<Box id="contact">
						<Heading as="h2" size="md" mb="3">
							Contact Information
						</Heading>
						<VStack align="stretch" gap="3">
							<Box
								bgGradient="to-br"
								gradientFrom="blue.50"
								gradientTo="purple.50"
								_dark={{ gradientFrom: 'blue.950/20', gradientTo: 'purple.950/20' }}
								rounded="xl"
								p="5"
								borderWidth="1px"
							>
								<Text fontWeight="semibold" fontSize="sm">
									{process.env.NEXT_PUBLIC_APP_NAME}
								</Text>
								<HStack gap="2" color="fg.muted" fontSize="sm">
									<Icon as={MailIcon} boxSize={4} color="realBlack" />
									<Link href={process.env.NEXT_PUBLIC_MAIL}>{process.env.NEXT_PUBLIC_MAIL}</Link>
								</HStack>
							</Box>
						</VStack>
					</Box>

					<Separator />

					<Box id="legal-info" pt="4">
						<HStack gap="2" mb="4">
							<Icon as={InfoIcon} boxSize={4} color="blue.500" />
							<Heading as="h2" size="md">
								Legal Notice
							</Heading>
						</HStack>

						<Stack gap="6" p="6" bg="bg.muted" rounded="xl" border="1px solid" borderColor="border.muted">
							<Box>
								<Text
									fontSize="xs"
									fontWeight="bold"
									color="fg.subtle"
									textTransform="uppercase"
									mb="1"
								>
									According to § 5 TMG
								</Text>
								<Text fontSize="sm" fontWeight="semibold">
									{process.env.NEXT_PUBLIC_APP_NAME}
								</Text>
								<Text fontSize="sm">{process.env.NEXT_PUBLIC_AUTHOR_NAME}</Text>
							</Box>

							<Box>
								<Text
									fontSize="xs"
									fontWeight="bold"
									color="fg.subtle"
									textTransform="uppercase"
									mb="1"
								>
									Contact
								</Text>
								<Link href={`mailto:${process.env.NEXT_PUBLIC_MAIL}`} fontSize="sm" color="blue.600">
									{process.env.NEXT_PUBLIC_MAIL}
								</Link>
							</Box>

							<Box>
								<Text
									fontSize="xs"
									fontWeight="bold"
									color="fg.subtle"
									textTransform="uppercase"
									mb="1"
								>
									VAT-ID
								</Text>
								<Text fontSize="sm">
									Sales tax identification number according to §27a Value Added Tax Act:
								</Text>
								<Text fontSize="sm" fontWeight="medium">
									{process.env.NEXT_PUBLIC_VAT_ID}
								</Text>
							</Box>

							<Box pt="2">
								<HStack gap="2">
									<Box px="2" py="0.5" bg="bg.emphasized" rounded="md" borderWidth="1px">
										<Text fontSize="10px" fontWeight="bold" color="fg.muted">
											SOFTWARE VERSION
										</Text>
									</Box>
									<Text fontSize="xs" color="fg.subtle">
										{wireframe.version !== '' ? `v${wireframe.version}` : ''}
									</Text>
								</HStack>
							</Box>
						</Stack>
					</Box>

					<Box pt="8" pb="4">
						<Text fontSize="xs" color="fg.subtle" textAlign="center">
							By using Imagemotion, you acknowledge that you have read, understood, and agree to be bound
							by these Terms of Service.
						</Text>
					</Box>
				</VStack>
			</Container>
		</Box>
	);
}
